import { TechnicianSelect, TimeRangeSelect, TimeRangeValue } from '@components';
import { CreateOrderWorkLog, Order, OrderWorkLog } from '@services';
import { useStore } from '@stores';
import { MINUTES_IN_DAY, formatDateWithOffset, formatLoggedTime, timeToMinutes } from '@utils';
import { DatePicker, Form, Input, message, Modal, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo } from 'react';

const { Text } = Typography;

/** Select value for "no service line" - antd treats undefined as "nothing chosen", so a sentinel is needed. */
const WHOLE_JOB = -1;

interface IProps {
  open: boolean;
  order: Order;
  editing: OrderWorkLog | undefined;
  currentUserId: number | undefined;
  /** The signed-in user name, for the case where they are not on the technician list. */
  currentUserName: string | undefined;
  canManage: boolean;
  onClose: () => void;
}

interface IFormValues {
  userId: number;
  serviceId: number;
  date: dayjs.Dayjs;
  /** 'HH:mm' strings from the shared time select; an empty end = still working. */
  times: TimeRangeValue;
  note?: string;
}

// TechnicianSelect reports through onSelect/onClear rather than onChange, so Form.Item needs
// this thin adapter to bind it as a field. The fallback option carries the name of whoever is
// already selected: the list is technicians only, and a manager logging their own time is not in
// it - without a matching option antd falls back to showing the raw user id.
const TechnicianField: React.FC<{
  value?: number;
  disabled?: boolean;
  fallback?: { label: string | undefined; value: number | undefined };
  onChange?: (value?: number) => void;
}> = ({ value, disabled, fallback, onChange }) => (
  <TechnicianSelect
    value={value}
    disabled={disabled}
    allowClear={false}
    defaultValue={fallback?.label ? [fallback] : undefined}
    style={{ width: '100%' }}
    onSelect={(id) => onChange?.(id)}
  />
);

// 'HH:mm' on the picked day.
const combine = (date: dayjs.Dayjs, time: string) =>
  date.startOf('day').add(timeToMinutes(time), 'minute');

// Length of a start/end pair of 'HH:mm'. An end before the start is read as the next day - a
// night job, or an entry opened yesterday and closed this morning - not as an error. Equal times
// are 0, which the form rejects.
const rangeMinutes = (start: string, end: string) => {
  const diff = timeToMinutes(end) - timeToMinutes(start);
  return diff < 0 ? diff + MINUTES_IN_DAY : diff;
};

const OrderWorkLogForm: React.FC<IProps> = ({
  open,
  order,
  editing,
  currentUserId,
  currentUserName,
  canManage,
  onClose,
}) => {
  const { orderStore } = useStore();
  const { orderWorkLogSaving } = orderStore;
  const [form] = Form.useForm<IFormValues>();

  const times = Form.useWatch('times', form);
  const previewMinutes =
    times?.startTime && times?.endTime
      ? rangeMinutes(times.startTime, times.endTime)
      : undefined;

  // "Whole job" first, then the lines currently on the order, plus the line an existing entry
  // points at when that service has since been removed - otherwise the select would show a blank.
  const serviceOptions = useMemo(() => {
    const options = [
      { value: WHOLE_JOB, label: 'Whole job (not a specific service)' },
      ...order.serviceOrders.map((line) => ({ value: line.serviceId, label: line.title })),
    ];
    if (editing?.serviceId != null && !options.some((o) => o.value === editing.serviceId)) {
      options.push({ value: editing.serviceId, label: `${editing.serviceTitle} (removed)` });
    }
    return options;
  }, [order.serviceOrders, editing]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.setFieldsValue({
        userId: editing.userId,
        serviceId: editing.serviceId ?? WHOLE_JOB,
        date: dayjs(editing.workedFrom),
        times: {
          startTime: dayjs(editing.workedFrom).format('HH:mm'),
          endTime: editing.workedTo ? dayjs(editing.workedTo).format('HH:mm') : undefined,
        },
        note: editing.note,
      });
    } else {
      // Default to the last hour, on the select's 15-minute grid. Rounded down: rounding up at
      // 23:50 would land on tomorrow, a date the picker does not allow. The date is the start's:
      // just after midnight the hour began yesterday and the end rolls over to today.
      const now = dayjs();
      const end = now.minute(Math.floor(now.minute() / 15) * 15).second(0).millisecond(0);
      const start = end.subtract(1, 'hour');
      const lines = order.serviceOrders;
      form.setFieldsValue({
        userId: currentUserId,
        serviceId: lines.length === 1 ? lines[0].serviceId : WHOLE_JOB,
        date: start,
        times: {
          startTime: start.format('HH:mm'),
          endTime: end.format('HH:mm'),
        },
        note: undefined,
      });
    }
  }, [open, editing, currentUserId, order.serviceOrders, form]);

  // Whoever the form opens on: the entry's technician when editing, the signed-in user otherwise.
  // Their name is known here, and it is the only way the select can show it - see TechnicianField.
  const selectedTechnician = editing
    ? { label: editing.user?.fullname, value: editing.userId }
    : { label: currentUserName, value: currentUserId };

  const handleOk = async () => {
    const values = await form.validateFields();
    const workedFrom = combine(values.date, values.times.startTime!);
    const minutes = values.times.endTime
      ? rangeMinutes(values.times.startTime!, values.times.endTime)
      : undefined;

    if (minutes === 0) {
      form.setFields([{ name: 'times', errors: ['End must differ from start'] }]);
      return;
    }

    const workedTo = minutes ? workedFrom.add(minutes, 'minute') : undefined;

    const wholeJob = values.serviceId === WHOLE_JOB;
    const payload: Omit<CreateOrderWorkLog, 'orderId'> & { userId: number } = {
      serviceId: wholeJob ? undefined : values.serviceId,
      userId: values.userId,
      workedFrom: formatDateWithOffset(workedFrom),
      workedTo: workedTo ? formatDateWithOffset(workedTo) : undefined,
      note: values.note?.trim() || undefined,
    };

    try {
      if (editing) {
        await orderStore.updateOrderWorkLog({ id: editing.id, ...payload });
      } else {
        await orderStore.createOrderWorkLog({ orderId: order.id, ...payload });
      }
      message.success(editing ? 'Time entry updated' : workedTo ? 'Time logged' : 'Started');
      onClose();
    } catch {
      // The http interceptor has already shown the server's message; keep the modal open.
    }
  };

  const durationHint =
    previewMinutes === undefined ? (
      <Text type="secondary">
        No end time: the entry stays open and keeps counting until you set one.
      </Text>
    ) : previewMinutes === 0 ? (
      <Text type="danger">End must differ from start</Text>
    ) : (
      <Text type="secondary">
        Duration: {formatLoggedTime(previewMinutes)}
        {times?.endTime! < times?.startTime! && ' (ends the next day)'}
      </Text>
    );

  return (
    <Modal
      open={open}
      title={editing ? 'Edit time entry' : 'Log time'}
      okText={editing ? 'Save' : 'Log time'}
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={orderWorkLogSaving}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="userId"
          label="Technician"
          rules={[{ required: true, message: 'Select a technician' }]}
        >
          <TechnicianField disabled={!canManage} fallback={selectedTechnician} />
        </Form.Item>
        <Form.Item
          name="serviceId"
          label="Service"
          rules={[{ required: true, message: 'Select a service line' }]}
        >
          <Select options={serviceOptions} placeholder="Which service was worked on" />
        </Form.Item>
        <Form.Item name="date" label="Date" rules={[{ required: true, message: 'Select a date' }]}>
          <DatePicker
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            disabledDate={(d) => d.isAfter(dayjs(), 'day')}
          />
        </Form.Item>
        <Form.Item
          name="times"
          label="Time (clear the end if still working)"
          rules={[
            {
              validator: (_, value: TimeRangeValue | undefined) =>
                value?.startTime ? Promise.resolve() : Promise.reject(new Error('Start time')),
            },
          ]}
        >
          <TimeRangeSelect allowOpenEnd placeholder={['From', 'To']} />
        </Form.Item>
        {durationHint}
        <Form.Item name="note" label="Note" style={{ marginTop: 12 }}>
          <Input.TextArea rows={3} maxLength={1024} placeholder="What was done (optional)" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default observer(OrderWorkLogForm);
